import React, { useEffect, useMemo, useState } from 'react'
import {
  MaterialReactTable,
  useMaterialReactTable,
  type MRT_ColumnDef,
  type MRT_ColumnFiltersState,
  type MRT_PaginationState,
  type MRT_SortingState,
  MRT_ColumnOrderState,
  MRT_ToggleFiltersButton,
  MRT_ShowHideColumnsButton,
  MRT_ToggleDensePaddingButton,
  MRT_TablePagination,
} from 'material-react-table'
import { orderByPosition } from '../../../utils/helper'
import { Box, lighten, Skeleton } from '@mui/material'
import { useSearchParams } from 'react-router-dom'

type Props = {
  titleText?: string
  prefix?: React.ReactNode
  actions?: any
  positionActionsColumn?: 'last' | 'first'
  data: any[]
  columns?: any
  loader?: boolean
  rowCount?: number
  getApiCall?: any
  columnPayload?: any
  manualPagination?: boolean
  initialState?: any
  state?: any
  height?: number | string
  isRowClickable?: boolean
  onRowClick?: (row: any) => void
}

const DEFAULT_BODY_HEIGHT = 'calc(100vh - 250px)' // fallback when no height prop is given

const CustomTable = ({
  titleText,
  prefix,
  actions,
  positionActionsColumn,
  data,
  columns,
  loader,
  rowCount,
  getApiCall,
  columnPayload,
  manualPagination,
  initialState,
  state,
  height,
  isRowClickable,
  onRowClick,
}: Props) => {
  const resolvedHeight = typeof height === 'number' ? `${height}px` : height || `${DEFAULT_BODY_HEIGHT}`

  const [searchParams, setSearchParams] = useSearchParams()
  const queryParamPage = searchParams.get('page') // Get the page from query parameter (@TODO this will be page number as a string like 2)

  const [columnFilters] = useState<MRT_ColumnFiltersState>([])
  const [columnOrders] = useState<MRT_ColumnOrderState>(orderByPosition(columnPayload?.column_data)?.map((c: any) => c?.column) || [])
  const [sorting] = useState<MRT_SortingState>([])
  const [isFirstTime, setIsFirstTime] = useState<boolean>(true)
  const [pagination, setPagination] = useState<MRT_PaginationState>({
    pageIndex: queryParamPage ? Number(queryParamPage) - 1 : 0, //TODO
    pageSize: 20,
  })

  useEffect(() => {
    if (queryParamPage) {
      setPagination({
        pageIndex: queryParamPage ? Number(queryParamPage) - 1 : 0, //TODO
        pageSize: 20,
      })
    }
  }, [])

  // Auto-generate columns if not passed
  const generatedColumns: MRT_ColumnDef<any>[] = useMemo(() => {
    if (columns && columns.length > 0) return columns
    if (!data || data.length === 0) return []
    const sample = data && data[0] ? data[0] : {}
    return Object.keys(sample || {}).map((key) => ({
      accessorKey: key,
      header: key.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase()),
      Cell:
        typeof sample[key] === 'boolean'
          ? ({ cell }) => <Box component="span">{cell.getValue<boolean>() ? 'Yes' : 'No'}</Box>
          : ({ cell }) => (cell.getValue<any>() && cell.getValue<any>()?.length > 0 ? cell.getValue<any>() : 'N/A'),
    }))
  }, [columns, data])

  useEffect(
    () => {
      if (getApiCall) {
        getApiCall({
          pagination: {
            ...pagination,
            pageIndex: pagination.pageIndex + 1,
          },
          columnFilters,
          sorting,
        })
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    manualPagination ? [pagination.pageIndex, pagination.pageSize] : [],
  )
  // This logic says when api gets call and data set then look for the page if there is any in query param and set that as a default page
  useEffect(() => {
    if (data?.length && queryParamPage && `${pagination?.pageIndex + 1}` === queryParamPage) {
      setIsFirstTime(false)
    }
  }, [data, pagination])

  const table = useMaterialReactTable({
    // core
    columns: generatedColumns,
    data,

    // layout/scroll
    enableStickyHeader: true, // official API for sticky header
    enableColumnResizing: true,
    columnResizeMode: 'onEnd',
    layoutMode: 'grid', // works with sticky header in MRT v3
    muiTableContainerProps: {
      sx: {
        maxHeight: resolvedHeight, // vertical scroll INSIDE table body
        overflowX: 'scroll',
        overflowY: 'scroll',
      },
    },

    // header styling
    muiTableHeadCellProps: {
      sx: {
        backgroundColor: 'secondary.main',
        zIndex: 3,
      },
    },

    // toolbars
    enableFullScreenToggle: false,
    enableGlobalFilter: false,
    enableDensityToggle: true,
    enableColumnOrdering: true,
    enableTopToolbar: true,
    enableBottomToolbar: true,

    // replace the default top toolbar so we can add our top scrollbar right under it
    renderTopToolbar: ({ table }) => (
      <Box
        sx={(theme) => ({
          backgroundColor: lighten(theme.palette.background.default, 0.05),
          display: 'flex',
          flexDirection: 'column',
          gap: '8px',
          p: '8px',
        })}
      >
        <Box sx={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', alignItems: 'center' }}>
        <Box className="text-l font-bold" sx={{display: 'flex', flex:1}}>{titleText ? titleText : ''}</Box>
          <Box sx={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            <MRT_ToggleFiltersButton table={table} />
            <MRT_ShowHideColumnsButton table={table} />
            <MRT_ToggleDensePaddingButton table={table} />
          </Box>
          <Box sx={{ display: 'flex', gap: '0.5rem' }}>{prefix}</Box>
        </Box>
      </Box>
    ),

    // replace the bottom toolbar so we can insert a bottom scrollbar above pagination
    renderBottomToolbar: ({ table }) => (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
        {/* Default pagination UI */}
        <MRT_TablePagination table={table} />
      </Box>
    ),

    // pagination
    paginationDisplayMode: 'pages',
    muiPaginationProps: {
      showFirstButton: false,
      showLastButton: false,
      color: 'primary',
      shape: 'rounded',
    },
    manualPagination: manualPagination || false,
    rowCount: rowCount || data?.length || 0,
    onPaginationChange: (updater: any) => {
      let newPage = queryParamPage ? Number(queryParamPage) : 1
      setPagination((old) => {
        const newState = typeof updater === 'function' ? updater(old) : updater
        // TODO here everytime page changed need this page on top as query param ?page={page}
        // 👇 your conditional logic
        if (queryParamPage && isFirstTime) {
          return { ...old, pageIndex: queryParamPage ? Number(queryParamPage) - 1 : 0 } // keep it fixed
        } else {
          newPage = newState.pageIndex + 1
          return newState
        }
      })

      setSearchParams((prev) => {
        const newParams = new URLSearchParams(prev)
        newParams.set('page', newPage.toString())
        return newParams
      }, { replace: true })
    },

    // actions
    positionActionsColumn: positionActionsColumn || 'last',
    enableRowActions: actions,
    renderRowActions: actions,

    // row click handling
    muiTableBodyRowProps: isRowClickable && onRowClick
      ? ({ row }) => ({
          onClick: () => onRowClick({ row }),
          sx: { cursor: 'pointer' },
        })
      : undefined,

    // initial/state
    initialState: {
      ...initialState,
      showGlobalFilter: false,
      columnOrder: columnOrders.length ? columnOrders : undefined,
    },
    state: {
      isLoading: loader,
      pagination,
      ...state,
    },

    // empty fallback
    renderEmptyRowsFallback: () => <Box className="py-10 text-center text-gray-500">No records to display</Box>,
  })

  const SkeletonLoader = () => {
    const breakpoints = ['xl', 'lg', 'md', '']
    return (
      <>
        <Box className="gap-2 w-full p-3 pb-1 pt-2 pr-10 flex justify-end">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} variant="text" height={42} className="w-[30px]" />
          ))}
        </Box>
        <Box className="border border-gray-200 rounded-md overflow-hidden">
          <Box className="gap-2 bg-gray-100 p-2 flex">
            {breakpoints.map((bp, index) => (
              <Skeleton key={index} variant="text" height={32} className={`w-full ${bp ? `hidden ${bp}:flex` : ''}`} />
            ))}
          </Box>
          {breakpoints.map((_, rowIdx) => (
            <Box className="gap-2 p-2 flex" key={rowIdx}>
              {breakpoints.map((bp, index) => (
                <Skeleton key={index} variant="text" height={28} className={`w-full ${bp ? `hidden ${bp}:flex` : ''}`} />
              ))}
            </Box>
          ))}
        </Box>
      </>
    )
  }

  return (
    <div className="w-full">
      <div className="custom-table-section">{!columns && loader ? <SkeletonLoader /> : <MaterialReactTable table={table} />}</div>
    </div>
  )
}

export default CustomTable
